import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { functions, db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import {
  Search, MapPin, Store, LogOut, Shield,
  Globe, EyeOff, Ban, History, RefreshCw, AlertTriangle, X, Check,
  User, Package, Building2, Plus, Trash2, Star, SlidersHorizontal
} from 'lucide-react';
import toast from 'react-hot-toast';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getLocationString = (loc) => {
  if (!loc) return null;
  if (typeof loc === 'string') return loc;
  if (typeof loc === 'object') {
    const parts = [loc.city, loc.country].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
  }
  return null;
};

const STATUS_CONFIG = {
  publish: {
    label: 'Publicado',
    badgeClass: 'bg-green-500/20 text-green-700 dark:text-green-400 border-green-500/30',
    dotClass: 'bg-green-400',
  },
  desactive: {
    label: 'Inactivo',
    badgeClass: 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-400 border-yellow-500/30',
    dotClass: 'bg-yellow-400',
  },
  suspend: {
    label: 'Suspendido',
    badgeClass: 'bg-red-500/20 text-red-700 dark:text-red-400 border-red-500/30',
    dotClass: 'bg-red-400',
  },
  setup: {
    label: 'Setup',
    badgeClass: 'bg-blue-500/20 text-blue-700 dark:text-blue-400 border-blue-500/30',
    dotClass: 'bg-blue-400',
  },
};

const getStatusConfig = (status) =>
  STATUS_CONFIG[status] || {
    label: status || 'Desconocido',
    badgeClass: 'bg-gray-500/20 text-content-muted border-line-active/30',
    dotClass: 'bg-gray-400',
  };

const StatusBadge = ({ status }) => {
  const cfg = getStatusConfig(status);
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded-full border ${cfg.badgeClass}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dotClass}`} />
      {cfg.label}
    </span>
  );
};

const formatDate = (timestamp) => {
  if (!timestamp) return '—';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return new Intl.DateTimeFormat('es', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).format(date);
};

// ─── Modal de confirmación ────────────────────────────────────────────────────

const StatusConfirmModal = ({ restaurant, newStatus, onConfirm, onCancel, loading }) => {
  const [confirmText, setConfirmText] = useState('');
  const isSuspend = newStatus === 'suspend';
  const isMatch = !isSuspend || confirmText.trim().toLowerCase() === restaurant.name.trim().toLowerCase();

  const cfg = getStatusConfig(newStatus);

  const actionLabels = {
    publish: 'publicar',
    desactive: 'desactivar',
    suspend: 'suspender',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-surface-base border border-line-strong rounded-2xl w-full max-w-md shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-line-subtle">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
              isSuspend ? 'bg-red-500/20' : newStatus === 'publish' ? 'bg-green-500/20' : 'bg-yellow-500/20'
            }`}>
              {newStatus === 'publish' ? (
                <Globe size={18} className="text-green-700 dark:text-green-400" />
              ) : newStatus === 'desactive' ? (
                <EyeOff size={18} className="text-yellow-700 dark:text-yellow-400" />
              ) : (
                <Ban size={18} className="text-red-700 dark:text-red-400" />
              )}
            </div>
            <div>
              <h3 className="text-content-primary font-semibold text-sm">
                Cambiar status a "{cfg.label}"
              </h3>
              <p className="text-content-subtle text-xs">{restaurant.name}</p>
            </div>
          </div>
          <button onClick={onCancel} className="text-content-subtle hover:text-content-primary transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {isSuspend ? (
            <>
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-500/30 rounded-xl p-4">
                <div className="flex items-start gap-2">
                  <AlertTriangle size={16} className="text-red-700 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-red-700 dark:text-red-300 text-sm">
                    Al suspender este restaurante, el owner y su equipo <strong>no podrán acceder al dashboard</strong> y el restaurante no será visible en Kumia-Web.
                  </p>
                </div>
              </div>
              <div>
                <label className="block text-sm text-content-muted mb-2">
                  Para confirmar, escribe exactamente el nombre del restaurante:
                  <span className="text-content-primary font-medium ml-1">"{restaurant.name}"</span>
                </label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder={restaurant.name}
                  className="w-full px-4 py-2.5 bg-surface-3 border border-line-active rounded-xl text-content-primary placeholder-content-disabled focus:outline-none focus:ring-2 focus:ring-red-500/50 focus:border-red-500/50 text-sm"
                  autoFocus
                />
              </div>
            </>
          ) : (
            <p className="text-content-secondary text-sm">
              ¿Deseas {actionLabels[newStatus]} el restaurante{' '}
              <span className="text-content-primary font-medium">"{restaurant.name}"</span>?{' '}
              {newStatus === 'publish'
                ? 'Será visible en Kumia-Web y el dashboard funcionará con normalidad.'
                : 'No será visible en Kumia-Web, pero el owner podrá seguir usando el dashboard.'}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-5 pt-0">
          <button
            onClick={onCancel}
            disabled={loading}
            className="flex-1 px-4 py-2.5 bg-surface-3 hover:bg-surface-5 text-content-secondary rounded-xl text-sm font-medium transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={() => onConfirm(isSuspend ? confirmText : undefined)}
            disabled={!isMatch || loading}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
              isSuspend
                ? 'bg-red-600 hover:bg-red-500 text-content-primary'
                : newStatus === 'publish'
                ? 'bg-green-600 hover:bg-green-500 text-content-primary'
                : 'bg-yellow-600 hover:bg-yellow-500 text-content-primary'
            }`}
          >
            {loading ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <Check size={16} />
            )}
            Confirmar
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Historial de logs ────────────────────────────────────────────────────────

const StatusHistoryPanel = ({ onClose }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        setLoading(true);
        const q = query(
          collection(db, 'restaurantStatusLogs'),
          orderBy('createdAt', 'desc'),
          limit(50)
        );
        const snap = await getDocs(q);
        setLogs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error('Error cargando logs:', err);
        toast.error('No se pudo cargar el historial.');
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, []);

  const statusArrow = (old, next) => (
    <span className="inline-flex items-center gap-1.5 text-xs">
      <StatusBadge status={old} />
      <span className="text-gray-600">→</span>
      <StatusBadge status={next} />
    </span>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-surface-base border border-line-strong rounded-2xl w-full max-w-2xl max-h-[80vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-line-subtle flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-purple-500/20 flex items-center justify-center">
              <History size={18} className="text-purple-700 dark:text-purple-400" />
            </div>
            <div>
              <h3 className="text-content-primary font-semibold">Historial de Acciones</h3>
              <p className="text-content-subtle text-xs">Últimas 50 acciones de cambio de status</p>
            </div>
          </div>
          <button onClick={onClose} className="text-content-subtle hover:text-content-primary transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 p-5">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-line-strong border-t-purple-400 rounded-full animate-spin" />
            </div>
          )}

          {!loading && logs.length === 0 && (
            <div className="text-center py-12">
              <History size={36} className="mx-auto text-gray-700 mb-3" />
              <p className="text-content-subtle">Aún no hay acciones registradas.</p>
            </div>
          )}

          {!loading && logs.length > 0 && (
            <div className="space-y-3">
              {logs.map((log) => (
                <div
                  key={log.id}
                  className="bg-surface-3/50 border border-line-strong/50 rounded-xl p-4"
                >
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <span className="font-medium text-content-primary text-sm truncate">
                      {log.restaurantName}
                    </span>
                    <span className="text-gray-600 text-xs whitespace-nowrap flex-shrink-0">
                      {formatDate(log.createdAt)}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    {statusArrow(log.oldStatus, log.newStatus)}
                    {log.requiresConfirmation && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-500/10 text-orange-700 dark:text-orange-400 text-xs border border-orange-500/20">
                        <AlertTriangle size={10} />
                        Confirmación requerida
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 mt-2 text-xs text-content-subtle">
                    <User size={12} />
                    <span>{log.performedByEmail}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Modal de acción de cadena (convertir / agregar sede) ──────────────────────

const ChainActionModal = ({ restaurant, mode, onConfirm, onCancel, loading }) => {
  const isConvert = mode === 'convert';
  const [value, setValue] = useState(isConvert ? (restaurant.name || '') : '');
  const canConfirm = value.trim().length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-surface-base border border-line-strong rounded-2xl w-full max-w-md shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-line-subtle">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-kumia-green/20">
              {isConvert ? <Building2 size={18} className="text-kumia-green" /> : <Plus size={18} className="text-kumia-green" />}
            </div>
            <div>
              <h3 className="text-content-primary font-semibold text-sm">
                {isConvert ? 'Convertir en cadena' : 'Agregar sede'}
              </h3>
              <p className="text-content-subtle text-xs">{restaurant.name}</p>
            </div>
          </div>
          <button onClick={onCancel} className="text-content-subtle hover:text-content-primary transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {isConvert ? (
            <p className="text-content-secondary text-sm">
              Este restaurante se convertirá en la <strong>sede principal</strong> de una nueva cadena.
              Podrás agregarle más sucursales después.
            </p>
          ) : (
            <p className="text-content-secondary text-sm">
              Se creará una nueva sucursal bajo la misma cadena y el mismo dueño.
              Nacerá en estado <strong>setup</strong> para completar su configuración.
            </p>
          )}
          <div>
            <label className="block text-sm text-content-muted mb-2">
              {isConvert ? 'Nombre de la marca' : 'Nombre de la sede'}
            </label>
            <input
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={isConvert ? 'Ej: La Pizzería' : 'Ej: Sucursal Las Condes'}
              className="w-full px-4 py-2.5 bg-surface-3 border border-line-active rounded-xl text-content-primary placeholder-content-disabled focus:outline-none focus:ring-2 focus:ring-kumia-green/50 focus:border-kumia-green/50 text-sm"
              autoFocus
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-5 pt-0">
          <button
            onClick={onCancel}
            disabled={loading}
            className="flex-1 px-4 py-2.5 bg-surface-3 hover:bg-surface-5 text-content-secondary rounded-xl text-sm font-medium transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={() => onConfirm(value.trim())}
            disabled={!canConfirm || loading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed bg-kumia-green hover:bg-green-500 text-black"
          >
            {loading ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <Check size={16} />
            )}
            {isConvert ? 'Convertir' : 'Crear sede'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Modal de eliminación (doble confirmación + nombre) ────────────────────────

const DeleteRestaurantModal = ({ restaurant, onConfirm, onCancel, loading }) => {
  const [step, setStep] = useState(1); // 1 = advertencia, 2 = escribir nombre
  const [confirmText, setConfirmText] = useState('');
  const isMatch = confirmText.trim().toLowerCase() === restaurant.name.trim().toLowerCase();
  const isChain = !!restaurant.orgId || restaurant.isPrimaryLocation === true;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-surface-base border border-line-strong rounded-2xl w-full max-w-md shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-line-subtle">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-red-500/20">
              <Trash2 size={18} className="text-red-700 dark:text-red-400" />
            </div>
            <div>
              <h3 className="text-content-primary font-semibold text-sm">Eliminar restaurante</h3>
              <p className="text-content-subtle text-xs">{restaurant.name}</p>
            </div>
          </div>
          <button onClick={onCancel} className="text-content-subtle hover:text-content-primary transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {isChain ? (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-500/30 rounded-xl p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle size={16} className="text-yellow-700 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                <p className="text-yellow-700 dark:text-yellow-300 text-sm">
                  Este restaurante pertenece a una <strong>cadena</strong> (o es sede principal). Desvincúlalo de la cadena antes de poder eliminarlo.
                </p>
              </div>
            </div>
          ) : step === 1 ? (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-500/30 rounded-xl p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle size={16} className="text-red-700 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <div className="text-red-700 dark:text-red-300 text-sm space-y-2">
                  <p>Vas a eliminar <strong>"{restaurant.name}"</strong>. Esta acción:</p>
                  <ul className="list-disc list-inside space-y-1 text-red-700/90 dark:text-red-300/90">
                    <li>Ocultará el restaurante de la plataforma.</li>
                    <li>Revocará el acceso del owner y de todo su equipo (se cerrará su sesión).</li>
                  </ul>
                </div>
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-sm text-content-muted mb-2">
                Para confirmar, escribe exactamente el nombre del restaurante:
                <span className="text-content-primary font-medium ml-1">"{restaurant.name}"</span>
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={restaurant.name}
                className="w-full px-4 py-2.5 bg-surface-3 border border-line-active rounded-xl text-content-primary placeholder-content-disabled focus:outline-none focus:ring-2 focus:ring-red-500/50 focus:border-red-500/50 text-sm"
                autoFocus
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-5 pt-0">
          <button
            onClick={onCancel}
            disabled={loading}
            className="flex-1 px-4 py-2.5 bg-surface-3 hover:bg-surface-5 text-content-secondary rounded-xl text-sm font-medium transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          {!isChain && step === 1 && (
            <button
              onClick={() => setStep(2)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all bg-red-600 hover:bg-red-500 text-white"
            >
              Continuar
            </button>
          )}
          {!isChain && step === 2 && (
            <button
              onClick={() => onConfirm(confirmText)}
              disabled={!isMatch || loading}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed bg-red-600 hover:bg-red-500 text-white"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <Trash2 size={16} />
              )}
              Eliminar
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Componente principal ─────────────────────────────────────────────────────

const SuperAdminRestaurantSelector = () => {
  const navigate = useNavigate();
  const { switchRestaurant, logout, currentUser } = useAuth();

  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showHistory, setShowHistory] = useState(false);

  // Estado del modal de cambio de status
  const [pendingChange, setPendingChange] = useState(null); // { restaurant, newStatus }
  const [changingStatus, setChangingStatus] = useState(false);

  // Dropdown de status abierto por restaurante
  const [openDropdown, setOpenDropdown] = useState(null);

  // Set de keys `${restaurantId}:${moduleKey}` actualmente toggleando (evita doble click).
  const [togglingModules, setTogglingModules] = useState(new Set());

  // Set de restaurantIds con Early Access en proceso de cambio (evita doble click).
  const [togglingEarlyAccess, setTogglingEarlyAccess] = useState(new Set());

  // Acción de cadena (sucursales): { restaurant, mode: 'convert' | 'addLocation' }
  const [chainAction, setChainAction] = useState(null);
  const [chainActionLoading, setChainActionLoading] = useState(false);

  // Eliminación de restaurante: { restaurant }
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Filtros (panel desplegable)
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilters, setStatusFilters] = useState([]); // ['publish', 'suspend', ...]
  const [typeFilters, setTypeFilters] = useState([]); // ['individual', 'primary', 'branch']

  const fetchRestaurants = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const getAllRestaurantsFn = httpsCallable(functions, 'restaurants-endpoints-getAllRestaurants');
      const result = await getAllRestaurantsFn();
      if (result.data.success) {
        setRestaurants(result.data.restaurants);
      }
    } catch (err) {
      console.error('Error cargando restaurantes:', err);
      setError('No se pudieron cargar los restaurantes.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRestaurants();
  }, [fetchRestaurants]);

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    if (!openDropdown) return;
    const handler = () => setOpenDropdown(null);
    window.addEventListener('click', handler);
    return () => window.removeEventListener('click', handler);
  }, [openDropdown]);

  // Cerrar panel de filtros al hacer click fuera
  useEffect(() => {
    if (!showFilters) return;
    const handler = () => setShowFilters(false);
    window.addEventListener('click', handler);
    return () => window.removeEventListener('click', handler);
  }, [showFilters]);

  // Determina el "tipo" de un restaurante para el filtro de cadena.
  const getRestaurantType = (r) => {
    if (!r.orgId) return 'individual';
    return r.isPrimaryLocation ? 'primary' : 'branch';
  };

  const filteredRestaurants = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return restaurants.filter((r) => {
      // Búsqueda por texto
      if (q) {
        const matchesText =
          r.name?.toLowerCase().includes(q) ||
          getLocationString(r.location)?.toLowerCase().includes(q) ||
          r.id?.toLowerCase().includes(q);
        if (!matchesText) return false;
      }
      // Filtro por estado
      if (statusFilters.length > 0 && !statusFilters.includes(r.status)) {
        return false;
      }
      // Filtro por tipo (cadena / individual)
      if (typeFilters.length > 0 && !typeFilters.includes(getRestaurantType(r))) {
        return false;
      }
      return true;
    });
  }, [restaurants, searchQuery, statusFilters, typeFilters]);

  const activeFilterCount = statusFilters.length + typeFilters.length;

  const toggleStatusFilter = (status) => {
    setStatusFilters((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );
  };

  const toggleTypeFilter = (type) => {
    setTypeFilters((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const clearFilters = () => {
    setStatusFilters([]);
    setTypeFilters([]);
  };

  const handleSelectRestaurant = async (restaurantId) => {
    await switchRestaurant(restaurantId);
    navigate(`/${restaurantId}`);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const handleStatusChangeClick = (e, restaurant, newStatus) => {
    e.stopPropagation();
    setOpenDropdown(null);
    setPendingChange({ restaurant, newStatus });
  };

  const handleConfirmStatusChange = async (confirmationPhrase) => {
    if (!pendingChange) return;
    const { restaurant, newStatus } = pendingChange;
    setChangingStatus(true);
    try {
      const updateStatusFn = httpsCallable(functions, 'restaurants-endpoints-updateRestaurantStatus');
      await updateStatusFn({
        restaurantId: restaurant.id,
        newStatus,
        confirmationPhrase,
      });
      const cfg = getStatusConfig(newStatus);
      toast.success(`"${restaurant.name}" ahora está ${cfg.label.toLowerCase()}.`);
      setPendingChange(null);
      // Actualizar estado local
      setRestaurants((prev) =>
        prev.map((r) => (r.id === restaurant.id ? { ...r, status: newStatus } : r))
      );
    } catch (err) {
      console.error('Error cambiando status:', err);
      const msg = err?.message || 'Error al cambiar el status.';
      toast.error(msg.replace('Firebase: ', '').replace(/\s*\(.*\)$/, ''));
    } finally {
      setChangingStatus(false);
    }
  };

  const handleConfirmChainAction = async (name) => {
    if (!chainAction) return;
    const { restaurant, mode } = chainAction;
    setChainActionLoading(true);
    try {
      if (mode === 'convert') {
        const fn = httpsCallable(functions, 'restaurants-endpoints-promoteOwnerToGroup');
        const res = await fn({ restaurantId: restaurant.id, brandName: name });
        const orgId = res.data?.orgId;
        toast.success(`"${restaurant.name}" ahora es una cadena.`);
        setRestaurants((prev) =>
          prev.map((r) =>
            r.id === restaurant.id ? { ...r, orgId: orgId || r.orgId, isPrimaryLocation: true } : r
          )
        );
        setChainAction(null);
      } else {
        const fn = httpsCallable(functions, 'restaurants-endpoints-createNewLocation');
        await fn({ orgId: restaurant.orgId, locationName: name });
        toast.success(`Nueva sede creada en "${restaurant.name}".`);
        setChainAction(null);
        await fetchRestaurants(); // refrescar para mostrar la nueva sede
      }
    } catch (err) {
      console.error('[SuperAdmin] chain action error:', err);
      const msg = err?.message || 'Error en la acción de cadena.';
      toast.error(msg.replace('Firebase: ', '').replace(/\s*\(.*\)$/, ''));
    } finally {
      setChainActionLoading(false);
    }
  };

  const handleConfirmDelete = async (confirmationPhrase) => {
    if (!pendingDelete) return;
    const { restaurant } = pendingDelete;
    setDeleting(true);
    try {
      const deleteFn = httpsCallable(functions, 'restaurants-endpoints-deleteRestaurant');
      const res = await deleteFn({ restaurantId: restaurant.id, confirmationPhrase });
      const cleaned = res.data?.cleanedUsers ?? 0;
      toast.success(`"${restaurant.name}" eliminado. Acceso revocado a ${cleaned} usuario(s).`);
      setPendingDelete(null);
      // Quitar la card del estado local.
      setRestaurants((prev) => prev.filter((r) => r.id !== restaurant.id));
    } catch (err) {
      console.error('[SuperAdmin] deleteRestaurant error:', err);
      const msg = err?.message || 'Error al eliminar el restaurante.';
      toast.error(msg.replace('Firebase: ', '').replace(/\s*\(.*\)$/, ''));
    } finally {
      setDeleting(false);
    }
  };

  const STATUS_ACTIONS = [
    { status: 'publish', label: 'Publicar', icon: Globe, color: 'text-green-700 dark:text-green-400 hover:bg-green-500/10' },
    { status: 'desactive', label: 'Desactivar', icon: EyeOff, color: 'text-yellow-700 dark:text-yellow-400 hover:bg-yellow-500/10' },
    { status: 'suspend', label: 'Suspender', icon: Ban, color: 'text-red-700 dark:text-red-400 hover:bg-red-500/10' },
  ];

  // Catálogo de módulos opcionales que el super admin puede activar/desactivar.
  // Se puede extender a futuro con otros módulos (ej: 'rewards', 'mixology', etc.).
  const MODULE_CATALOG = [
    { key: 'inventory', label: 'Inventario', icon: Package, description: 'Gestión de insumos, recetas y anomalías.' },
  ];

  const handleToggleModule = async (e, restaurant, moduleKey) => {
    e.stopPropagation();
    const currentValue = restaurant.enabledModules?.[moduleKey] === true;
    const nextValue = !currentValue;
    const toggleKey = `${restaurant.id}:${moduleKey}`;

    if (togglingModules.has(toggleKey)) return;

    setTogglingModules((prev) => {
      const next = new Set(prev);
      next.add(toggleKey);
      return next;
    });

    try {
      const toggleFn = httpsCallable(functions, 'restaurants-endpoints-toggleRestaurantModule');
      await toggleFn({ restaurantId: restaurant.id, moduleKey, enabled: nextValue });

      setRestaurants((prev) =>
        prev.map((r) =>
          r.id === restaurant.id
            ? { ...r, enabledModules: { ...(r.enabledModules || {}), [moduleKey]: nextValue } }
            : r
        )
      );

      const label = MODULE_CATALOG.find((m) => m.key === moduleKey)?.label || moduleKey;
      toast.success(
        nextValue
          ? `"${restaurant.name}": módulo ${label} activado.`
          : `"${restaurant.name}": módulo ${label} desactivado.`
      );
    } catch (err) {
      console.error('[SuperAdmin] toggleRestaurantModule error:', err);
      const msg = err?.message || 'Error al cambiar el módulo.';
      toast.error(msg.replace('Firebase: ', '').replace(/\s*\(.*\)$/, ''));
    } finally {
      setTogglingModules((prev) => {
        const next = new Set(prev);
        next.delete(toggleKey);
        return next;
      });
    }
  };

  // Early Access: activa/desactiva el plan EARLY_ADOPTER ($250 + IVA por orden).
  // Reusa la callable de billing (solo super admin). Al desactivar, vuelve a STARTER.
  const handleToggleEarlyAccess = async (e, restaurant) => {
    e.stopPropagation();
    if (togglingEarlyAccess.has(restaurant.id)) return;

    const isEarly = restaurant.subscriptionPlanId === 'EARLY_ADOPTER';
    const nextPlan = isEarly ? 'STARTER' : 'EARLY_ADOPTER';

    setTogglingEarlyAccess((prev) => {
      const next = new Set(prev);
      next.add(restaurant.id);
      return next;
    });

    try {
      const fn = httpsCallable(functions, 'billing-endpoints-updateRestaurantSubscription');
      await fn({ restaurantId: restaurant.id, planId: nextPlan });

      setRestaurants((prev) =>
        prev.map((r) =>
          r.id === restaurant.id ? { ...r, subscriptionPlanId: nextPlan } : r
        )
      );

      toast.success(
        nextPlan === 'EARLY_ADOPTER'
          ? `"${restaurant.name}": Early Access activado ($250 + IVA por orden).`
          : `"${restaurant.name}": Early Access desactivado (vuelve a Starter).`
      );
    } catch (err) {
      console.error('[SuperAdmin] toggleEarlyAccess error:', err);
      const msg = err?.message || 'Error al cambiar Early Access.';
      toast.error(msg.replace('Firebase: ', '').replace(/\s*\(.*\)$/, ''));
    } finally {
      setTogglingEarlyAccess((prev) => {
        const next = new Set(prev);
        next.delete(restaurant.id);
        return next;
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-surface-base via-surface-app to-surface-3">
      {/* Modal de confirmación */}
      {pendingChange && (
        <StatusConfirmModal
          restaurant={pendingChange.restaurant}
          newStatus={pendingChange.newStatus}
          onConfirm={handleConfirmStatusChange}
          onCancel={() => setPendingChange(null)}
          loading={changingStatus}
        />
      )}

      {/* Panel de historial */}
      {showHistory && <StatusHistoryPanel onClose={() => setShowHistory(false)} />}

      {/* Modal de acción de cadena (convertir / agregar sede) */}
      {chainAction && (
        <ChainActionModal
          restaurant={chainAction.restaurant}
          mode={chainAction.mode}
          onConfirm={handleConfirmChainAction}
          onCancel={() => setChainAction(null)}
          loading={chainActionLoading}
        />
      )}

      {/* Modal de eliminación */}
      {pendingDelete && (
        <DeleteRestaurantModal
          restaurant={pendingDelete.restaurant}
          onConfirm={handleConfirmDelete}
          onCancel={() => setPendingDelete(null)}
          loading={deleting}
        />
      )}

      {/* Header */}
      <header className="border-b border-line-subtle bg-surface-chrome/80 dark:bg-black/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src={`${process.env.PUBLIC_URL || ''}/assets/kumia-logo.webp`}
              alt="KumIA"
              className="w-10 h-10"
              style={{ filter: 'drop-shadow(0 0 10px rgba(122, 201, 67, 0.5))' }}
            />
            <div>
              <h1 className="text-xl font-bold text-content-primary">
                <span>Kum</span><span className="text-kumia-green">IA</span>
              </h1>
              <div className="flex items-center gap-1.5">
                <Shield size={12} className="text-kumia-green" />
                <span className="text-xs text-kumia-green font-medium">Super Admin</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-content-muted hidden sm:inline">{currentUser?.email}</span>
            <button
              onClick={() => setShowHistory(true)}
              className="flex items-center gap-2 bg-surface-3 hover:bg-surface-5 text-content-secondary hover:text-content-primary px-3 py-2 rounded-lg font-medium text-sm transition-all duration-200 border border-line-strong"
              title="Historial de acciones"
            >
              <History size={16} />
              <span className="hidden sm:inline">Historial</span>
            </button>
            <button
              onClick={fetchRestaurants}
              disabled={loading}
              className="flex items-center gap-2 bg-surface-3 hover:bg-surface-5 text-content-secondary hover:text-content-primary px-3 py-2 rounded-lg text-sm transition-all duration-200 border border-line-strong disabled:opacity-50"
              title="Recargar"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 bg-red-600/20 hover:bg-red-600/30 text-red-700 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 px-3 py-2 rounded-lg font-medium text-sm transition-all duration-200 border border-red-600/30"
            >
              <LogOut size={16} />
              <span className="hidden sm:inline">Cerrar Sesión</span>
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-content-primary mb-2">Gestión de Restaurantes</h2>
          <p className="text-content-muted">
            Selecciona un restaurante para acceder a su dashboard o gestiona su status de publicación.
          </p>
        </div>

        {/* Leyenda de estados */}
        <div className="flex flex-wrap gap-3 mb-6">
          {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
            <div key={key} className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full border ${cfg.badgeClass}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${cfg.dotClass}`} />
              {cfg.label}
            </div>
          ))}
          <span className="text-xs text-gray-600 self-center ml-1">
            {restaurants.length} restaurantes totales
          </span>
        </div>

        {/* Search + Filtros */}
        <div className="flex items-stretch gap-3 mb-6">
          <div className="relative flex-1">
            <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-content-subtle" />
            <input
              type="text"
              placeholder="Buscar por nombre, ubicación o ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-24 py-3 bg-surface-3/50 border border-line-strong rounded-xl text-content-primary placeholder-content-subtle focus:outline-none focus:ring-2 focus:ring-kumia-green/50 focus:border-kumia-green/50 transition-all"
            />
            {restaurants.length > 0 && (
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-content-subtle">
                {filteredRestaurants.length} de {restaurants.length}
              </span>
            )}
          </div>

          {/* Botón Filtros + panel */}
          <div className="relative">
            <button
              onClick={(e) => { e.stopPropagation(); setShowFilters((v) => !v); }}
              className={`h-full flex items-center gap-2 px-4 rounded-xl text-sm font-medium border transition-all
                ${activeFilterCount > 0 || showFilters
                  ? 'bg-kumia-green/15 border-kumia-green/40 text-kumia-green'
                  : 'bg-surface-3/50 border-line-strong text-content-secondary hover:text-content-primary hover:border-line-active'
                }`}
            >
              <SlidersHorizontal size={16} />
              <span className="hidden sm:inline">Filtros</span>
              {activeFilterCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-kumia-green text-black text-[11px] font-bold">
                  {activeFilterCount}
                </span>
              )}
            </button>

            {showFilters && (
              <div
                onClick={(e) => e.stopPropagation()}
                className="absolute right-0 top-full mt-2 w-72 bg-surface-base border border-line-strong rounded-2xl shadow-2xl z-30 p-4"
              >
                {/* Estado */}
                <div className="mb-4">
                  <div className="text-[10px] uppercase tracking-wide text-content-subtle mb-2">Estado</div>
                  <div className="flex flex-wrap gap-1.5">
                    {['publish', 'desactive', 'suspend', 'setup'].map((status) => {
                      const cfg = getStatusConfig(status);
                      const active = statusFilters.includes(status);
                      return (
                        <button
                          key={status}
                          onClick={() => toggleStatusFilter(status)}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-all
                            ${active
                              ? `${cfg.badgeClass}`
                              : 'bg-surface-3/50 border-line-strong/50 text-content-subtle hover:text-content-secondary hover:border-line-active'
                            }`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${cfg.dotClass}`} />
                          {cfg.label}
                          {active && <Check size={11} />}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Tipo */}
                <div className="mb-4">
                  <div className="text-[10px] uppercase tracking-wide text-content-subtle mb-2">Tipo</div>
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      { key: 'individual', label: 'Individual', icon: Store },
                      { key: 'primary', label: 'Sede principal', icon: Building2 },
                      { key: 'branch', label: 'Sede de cadena', icon: Building2 },
                    ].map(({ key, label, icon: Icon }) => {
                      const active = typeFilters.includes(key);
                      return (
                        <button
                          key={key}
                          onClick={() => toggleTypeFilter(key)}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-all
                            ${active
                              ? 'bg-kumia-green/15 border-kumia-green/40 text-kumia-green'
                              : 'bg-surface-3/50 border-line-strong/50 text-content-subtle hover:text-content-secondary hover:border-line-active'
                            }`}
                        >
                          <Icon size={12} />
                          {label}
                          {active && <Check size={11} />}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between pt-3 border-t border-line-subtle">
                  <span className="text-xs text-content-subtle">
                    {filteredRestaurants.length} resultado{filteredRestaurants.length === 1 ? '' : 's'}
                  </span>
                  <button
                    onClick={clearFilters}
                    disabled={activeFilterCount === 0}
                    className="text-xs font-medium text-content-secondary hover:text-content-primary transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Limpiar filtros
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="kumia-spinner">
              <div className="spinner-ring"></div>
              <div className="spinner-text">
                <span className="text-content-primary">Kum</span>
                <span className="text-kumia-green">IA</span>
              </div>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-500/30 rounded-xl p-6 text-center">
            <p className="text-red-700 dark:text-red-400">{error}</p>
            <button
              onClick={fetchRestaurants}
              className="mt-4 px-4 py-2 bg-red-600/20 text-red-700 dark:text-red-400 rounded-lg hover:bg-red-600/30 transition-colors border border-red-600/30"
            >
              Reintentar
            </button>
          </div>
        )}

        {/* Restaurant Grid */}
        {!loading && !error && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredRestaurants.map((restaurant) => (
              <div
                key={restaurant.id}
                className="group bg-surface-3/40 hover:bg-surface-3/60 border border-line-strong/50 hover:border-line-active/60 rounded-xl overflow-visible transition-all duration-200"
              >
                {/* Clickable zone → entra al dashboard */}
                <button
                  onClick={() => handleSelectRestaurant(restaurant.id)}
                  className="w-full p-5 text-left flex items-start gap-4"
                >
                  {restaurant.logo ? (
                    <img
                      src={restaurant.logo}
                      alt={restaurant.name}
                      className="w-12 h-12 rounded-lg object-cover bg-surface-5 flex-shrink-0"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-surface-5/50 flex items-center justify-center flex-shrink-0">
                      <Store size={24} className="text-content-subtle group-hover:text-kumia-green transition-colors" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-content-primary group-hover:text-kumia-green transition-colors truncate">
                      {restaurant.name}
                    </h3>
                    {getLocationString(restaurant.location) && (
                      <div className="flex items-center gap-1 mt-1 text-sm text-content-subtle">
                        <MapPin size={14} className="flex-shrink-0" />
                        <span className="truncate">{getLocationString(restaurant.location)}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <StatusBadge status={restaurant.status} />
                      <span className="text-xs text-gray-600 truncate">{restaurant.id}</span>
                    </div>
                  </div>
                </button>

                {/* Controles de status */}
                <div className="px-4 pb-3 flex items-center gap-2">
                  <div className="flex gap-1.5 flex-1">
                    {STATUS_ACTIONS.map(({ status, label, icon: Icon, color }) => (
                      <button
                        key={status}
                        onClick={(e) => handleStatusChangeClick(e, restaurant, status)}
                        disabled={restaurant.status === status}
                        title={label}
                        className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium border transition-all duration-150
                          ${restaurant.status === status
                            ? 'opacity-30 cursor-not-allowed bg-surface-3 border-line-strong text-content-subtle'
                            : `bg-surface-3/70 border-line-strong/50 ${color} border-transparent hover:border-current`
                          }`}
                      >
                        <Icon size={13} />
                        <span className="hidden sm:inline">{label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Módulos opcionales */}
                <div className="px-4 pb-4">
                  <div className="text-[10px] uppercase tracking-wide text-content-subtle mb-1.5">Módulos</div>
                  <div className="flex flex-wrap gap-1.5">
                    {MODULE_CATALOG.map(({ key: modKey, label, icon: Icon, description }) => {
                      const active = restaurant.enabledModules?.[modKey] === true;
                      const toggleKey = `${restaurant.id}:${modKey}`;
                      const isToggling = togglingModules.has(toggleKey);
                      return (
                        <button
                          key={modKey}
                          onClick={(e) => handleToggleModule(e, restaurant, modKey)}
                          disabled={isToggling}
                          title={`${description} Click para ${active ? 'desactivar' : 'activar'}.`}
                          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-all duration-150
                            ${active
                              ? 'bg-kumia-green/15 border-kumia-green/40 text-kumia-green hover:bg-kumia-green/25'
                              : 'bg-surface-3/50 border-line-strong/50 text-content-subtle hover:border-line-active hover:text-content-secondary'
                            }
                            ${isToggling ? 'opacity-50 cursor-wait' : ''}`}
                        >
                          <Icon size={12} />
                          <span>{label}</span>
                          {active && <Check size={11} />}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Early Access (plan Early Adopter $250 + IVA por orden) */}
                <div className="px-4 pb-4">
                  <div className="text-[10px] uppercase tracking-wide text-content-subtle mb-1.5">Early Access</div>
                  {(() => {
                    const isEarly = restaurant.subscriptionPlanId === 'EARLY_ADOPTER';
                    const isToggling = togglingEarlyAccess.has(restaurant.id);
                    return (
                      <button
                        onClick={(e) => handleToggleEarlyAccess(e, restaurant)}
                        disabled={isToggling}
                        title={isEarly
                          ? 'Early Access activo ($250 + IVA por orden). Click para revertir a Starter.'
                          : 'Activar Early Access forever ($250 + IVA por orden). Click para activar.'}
                        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-all duration-150
                          ${isEarly
                            ? 'bg-amber-500/15 border-amber-500/40 text-amber-700 dark:text-amber-400 hover:bg-amber-500/25'
                            : 'bg-surface-3/50 border-line-strong/50 text-content-subtle hover:border-amber-500/40 hover:text-amber-600 dark:hover:text-amber-400'
                          }
                          ${isToggling ? 'opacity-50 cursor-wait' : ''}`}
                      >
                        <Star size={12} />
                        <span>{isEarly ? 'Early Access · $250' : 'Activar Early Access'}</span>
                        {isEarly && <Check size={11} />}
                      </button>
                    );
                  })()}
                </div>

                {/* Cadena / Sucursales */}
                <div className="px-4 pb-4">
                  <div className="text-[10px] uppercase tracking-wide text-content-subtle mb-1.5">Cadena</div>
                  {restaurant.orgId ? (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border bg-kumia-green/15 border-kumia-green/40 text-kumia-green">
                        <Building2 size={12} />
                        {restaurant.isPrimaryLocation ? 'Sede principal' : 'Sede'}
                        {restaurant.locationName ? ` · ${restaurant.locationName}` : ''}
                      </span>
                      <button
                        onClick={(e) => { e.stopPropagation(); setChainAction({ restaurant, mode: 'addLocation' }); }}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border bg-surface-3/50 border-line-strong/50 text-content-subtle hover:border-kumia-green/40 hover:text-kumia-green transition-all"
                      >
                        <Plus size={12} />
                        Agregar sede
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={(e) => { e.stopPropagation(); setChainAction({ restaurant, mode: 'convert' }); }}
                      disabled={!restaurant.ownerId}
                      title={!restaurant.ownerId ? 'El restaurante no tiene dueño asignado' : 'Convertir este restaurante en una cadena'}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border bg-surface-3/50 border-line-strong/50 text-content-subtle hover:border-kumia-green/40 hover:text-kumia-green transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Building2 size={12} />
                      Convertir en cadena
                    </button>
                  )}
                </div>

                {/* Zona peligrosa: eliminar */}
                <div className="px-4 pb-4 pt-1 border-t border-line-strong/40 mt-1">
                  <button
                    onClick={(e) => { e.stopPropagation(); setPendingDelete({ restaurant }); }}
                    disabled={!!restaurant.orgId || restaurant.isPrimaryLocation === true}
                    title={
                      restaurant.orgId || restaurant.isPrimaryLocation === true
                        ? 'Desvincula el restaurante de la cadena antes de eliminarlo'
                        : 'Eliminar restaurante'
                    }
                    className="w-full flex items-center justify-center gap-1.5 px-2.5 py-1.5 mt-2 rounded-lg text-xs font-medium border bg-surface-3/50 border-line-strong/50 text-content-subtle hover:border-red-500/50 hover:text-red-600 dark:hover:text-red-400 transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-line-strong/50 disabled:hover:text-content-subtle"
                  >
                    <Trash2 size={13} />
                    Eliminar restaurante
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && filteredRestaurants.length === 0 && (
          <div className="text-center py-20">
            <Store size={48} className="mx-auto text-gray-600 mb-4" />
            <p className="text-content-muted text-lg">
              {searchQuery || activeFilterCount > 0
                ? 'No se encontraron restaurantes con ese filtro.'
                : 'No hay restaurantes en el sistema.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SuperAdminRestaurantSelector;
